import React, { useState, useEffect } from 'react';
import { 
    Layout, 
    Card, 
    Row, 
    Col, 
    Table, 
    Tag, 
    Button, 
    Space, 
    Select, 
    message, 
    DatePicker,
    Typography 
} from 'antd';
import { 
    SearchOutlined, 
    ClearOutlined, 
    FileExcelOutlined, 
    ArrowLeftOutlined,
    FieldTimeOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import Headerdiv from "../layout/header";
import Footerdiv from "../layout/footer";
import axiosClient from '../../api/axiosClient';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const { Content } = Layout;
const { RangePicker } = DatePicker;

const InventoryActivityReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [filters, setFilters] = useState({
        item_ids: [],
        type: '',
        status: '',
        dateRange: [null, null]
    });

    // Fetch All Inventory Items for Multi-Select
    useEffect(() => {
        const fetchInventory = async () => {
            try {
                const response = await axiosClient.get('/inventory/get_all_inventory_dropdown.php');
                if (response.data.status === 'success') {
                    setInventoryItems(response.data.data);
                }
            } catch (error) {
                console.error('Fetch inventory dropdown error:', error);
                message.error('Failed to load inventory items.');
            }
        };
        fetchInventory();
    }, []);

    const columns = [
        {
            title: 'Item Code',
            dataIndex: 'item_code',
            key: 'item_code',
            width: 120,
        },
        {
            title: 'Item Name',
            dataIndex: 'item_name',
            key: 'item_name',
            width: 180,
        },
        {
            title: 'Record Type',
            dataIndex: 'type',
            key: 'type',
            width: 130,
            render: (type) => (
                <Tag color={type === 'Breakdown' ? 'red' : type === 'Service' ? 'blue' : 'orange'}>
                    {type}
                </Tag>
            )
        },
        {
            title: 'Record Date',
            dataIndex: 'date',
            key: 'date',
            width: 180,
            render: (date) => date ? dayjs(date).format('DD MMM YYYY hh:mm A') : 'N/A'
        },
        {
            title: 'Record Status',
            dataIndex: 'record_status',
            key: 'record_status',
            width: 120,
            render: (status) => (
                <Tag color={status === 'Open' ? 'processing' : 'success'}>
                    {status}
                </Tag>
            )
        },
        {
            title: 'Record Name',
            dataIndex: 'note',
            key: 'note',
            width: 200,
        },
        {
            title: 'Record Description',
            dataIndex: 'description',
            key: 'description',
            render: (desc) => (
                <div 
                    dangerouslySetInnerHTML={{ __html: desc }} 
                    style={{ maxHeight: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                />
            )
        }
    ];

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.item_ids.length > 0) params.append('item_ids', filters.item_ids.join(','));
            if (filters.type) params.append('type', filters.type);
            if (filters.status) params.append('status', filters.status);
            
            if (filters.dateRange[0] && filters.dateRange[1]) {
                params.append('start_date', filters.dateRange[0].format('YYYY-MM-DD'));
                params.append('end_date', filters.dateRange[1].format('YYYY-MM-DD'));
            }

            const response = await axiosClient.get(`/reports/get_inventory_activity_report.php?${params.toString()}`);
            if (response.data.status === 'success') {
                setData(response.data.data || []);
                if (response.data.data.length === 0) {
                    message.info('No activity records found for the selected criteria.');
                }
            }
        } catch (error) {
            console.error('Fetch report error:', error);
            message.error('Failed to fetch report data.');
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setFilters({
            item_ids: [],
            type: '',
            status: '',
            dateRange: [null, null]
        });
        setData([]);
    };

    const exportToExcel = () => {
        if (data.length === 0) return;
        
        const worksheetData = data.map(item => ({
            'Item Code': item.item_code,
            'Item Name': item.item_name,
            'Record Type': item.type,
            'Record Date': item.date ? dayjs(item.date).format('DD MMM YYYY hh:mm A') : 'N/A',
            'Record Status': item.record_status,
            'Record Name': item.note,
            'Record Description': item.description ? item.description.replace(/<[^>]*>?/gm, '') : ''
        }));

        const ws = XLSX.utils.json_to_sheet(worksheetData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Inventory Activity");
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const finalData = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' });
        saveAs(finalData, `Inventory_Activity_Report_${dayjs().format('YYYYMMDD')}.xlsx`);
    };

    return (
        <Layout style={{ minHeight: '100vh', backgroundColor: '#f0f2f5' }}>
            <Headerdiv />
            <Content style={{ padding: '24px' }}>
                <div style={{ marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports')}>Back to Reports</Button>
                </div>

                {/* Filter Card */}
                <Card 
                    title={
                        <Space>
                            <FieldTimeOutlined />
                            <span>Inventory Activity Report</span>
                        </Space>
                    }
                    extra={
                        <Space wrap>
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />} 
                                onClick={fetchReport} 
                                loading={loading}
                            >
                                Generate
                            </Button>
                            <Button icon={<ClearOutlined />} onClick={handleClear}>Clear</Button>
                        </Space>
                    }
                    style={{ marginBottom: 24 }}
                >
                    <Row gutter={[16, 16]} align="bottom">
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ marginBottom: 4, fontWeight: 500, fontSize: '12px' }}>Record Type</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="All Types"
                                value={filters.type}
                                onChange={(val) => setFilters({...filters, type: val})}
                            >
                                <Select.Option value="">All Types</Select.Option>
                                <Select.Option value="Breakdown">Breakdown</Select.Option>
                                <Select.Option value="Repair">Repair</Select.Option>
                                <Select.Option value="Service">Service</Select.Option>
                                <Select.Option value="Other">Other</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={12} md={4}>
                            <div style={{ marginBottom: 4, fontWeight: 500, fontSize: '12px' }}>Record Status</div>
                            <Select 
                                style={{ width: '100%' }} 
                                placeholder="All Statuses"
                                value={filters.status}
                                onChange={(val) => setFilters({...filters, status: val})}
                            >
                                <Select.Option value="">All Statuses</Select.Option>
                                <Select.Option value="Open">Open</Select.Option>
                                <Select.Option value="Closed">Closed</Select.Option>
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500, fontSize: '12px' }}>Select Item(s)</div>
                            <Select 
                                mode="multiple"
                                style={{ width: '100%' }} 
                                placeholder="All Items"
                                value={filters.item_ids}
                                onChange={(val) => setFilters({...filters, item_ids: val})}
                                maxTagCount="responsive"
                                allowClear
                            >
                                {inventoryItems.map(item => (
                                    <Select.Option key={item.id} value={item.id}>
                                        {item.item_code} - {item.value}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                            <div style={{ marginBottom: 4, fontWeight: 500, fontSize: '12px' }}>Date Range</div>
                            <RangePicker 
                                style={{ width: '100%' }} 
                                value={filters.dateRange}
                                onChange={(dates) => setFilters({...filters, dateRange: dates})}
                            />
                        </Col>
                    </Row>
                </Card>

                {/* Results Card */}
                <Card 
                    title={`Report Results ${data.length > 0 ? `(${data.length} records)` : ''}`}
                    extra={
                        data.length > 0 && (
                            <Button 
                                icon={<FileExcelOutlined />} 
                                onClick={exportToExcel}
                                style={{ backgroundColor: '#217346', color: '#fff', borderColor: '#217346' }}
                            >
                                Export to Excel
                            </Button>
                        )
                    }
                >
                    <Table 
                        dataSource={data}
                        columns={columns}
                        rowKey="id"
                        loading={loading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                        }}
                        bordered
                        size="small"
                        locale={{ emptyText: 'No data. Adjust filters and click Generate.' }}
                        scroll={{ x: 1200 }}
                    />
                </Card>
            </Content>
            <Footerdiv />
        </Layout>
    );
};

export default InventoryActivityReport;
